import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import CustomerService from '@/services/customerService';

interface UserRole {
  id: string;
  user_id: string;
  role: 'super_admin' | 'admin' | 'branch_admin' | 'branch_staff' | 'staff' | 'customer' | 'rider' | 'kitchen' | 'customer_service' | 'dispatcher';
  scope?: 'global' | 'branch' | 'zone';
  branch_id?: string;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signOut: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
  userRoles: UserRole[] | null;
  isSuperAdmin: boolean;
  isRider: boolean;
  isEmailConfirmed: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  refreshUserRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRoles, setUserRoles] = useState<UserRole[] | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isRider, setIsRider] = useState(false);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const handleAuthChange = async (event: string, session: Session | null) => {
      if (!isMounted) return;
      
      console.log('🔐 Auth state changed:', { event, hasSession: !!session, userId: session?.user?.id });
      
      setSession(session);
      setUser(session?.user ?? null);
      setIsEmailConfirmed(!!session?.user?.email_confirmed_at);
      
      if (session?.user) {
        try {
          const roles = await loadUserRoles(session.user.id);
          if (isMounted) {
            console.log(roles, "roles");
            setUserRoles(roles);
            // Admin includes admins, staff, riders, kitchen, customer_service, dispatcher (non-customers)
            const isUserAdmin = roles.some(r => ['admin', 'super_admin', 'branch_admin', 'staff', 'branch_staff', 'rider', 'kitchen', 'customer_service', 'dispatcher'].includes(r.role));
            setIsAdmin(isUserAdmin);
            const isUserSuperAdmin = roles.some(r => r.role === 'super_admin');
            setIsSuperAdmin(isUserSuperAdmin);
            const isUserRider = roles.some(r => r.role === 'rider');
            setIsRider(isUserRider);
            
            // Auto-link customer on login
            if (event === 'SIGNED_IN' && !isUserAdmin && !isUserSuperAdmin) {
              try {
                await CustomerService.autoLinkUserOnLogin(session.user.id, session.user);
              } catch (error) {
                console.error('Error auto-linking customer on login:', error);
                // Don't fail login if customer linking fails
              }
            }
            
            // Disabled automatic redirect to prevent conflicts with manual navigation from AuthModal
            // if (event === 'INITIAL_SESSION') {
            //   handleUserRedirect(session.user, isUserAdmin, isUserRider);
            // }
          }
        } catch (error) {
          console.error('Error loading user roles:', error);
          if (isMounted) {
            setIsAdmin(false);
            setIsSuperAdmin(false);
            setIsRider(false);
            setUserRoles(null);
          }
        }
      } else {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsRider(false);
        setUserRoles(null);
      }
      
      if (isMounted) setLoading(false);
    };

    // Get initial session
    supabase.auth.getSession()?.then(({ data: { session } }) => {
      handleAuthChange('INITIAL_SESSION', session);
    }).catch(() => {
      // Ignore auth errors in tests
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        handleAuthChange(event, session);
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const loadUserRoles = async (userId: string): Promise<UserRole[]> => {
    try {
      // Load user roles from database
      const { data: roles, error } = await supabase
        .from("user_roles")
        .select("*")
        .eq("user_id", userId);
      
      if (error) {
        console.error('Error loading user roles:', error);
        return [];
      }
      
      return roles || [];
    } catch (error) {
      console.error('Error in loadUserRoles:', error);
      return [];
    }
  };

  const refreshUserRoles = async (): Promise<void> => {
    if (!user?.id) return;
    
    try {
      const roles = await loadUserRoles(user.id);
      setUserRoles(roles);
      // Admin includes admins, staff, riders, kitchen, customer_service, dispatcher (non-customers)
      const isUserAdmin = roles.some(r => ['admin', 'super_admin', 'branch_admin', 'staff', 'branch_staff', 'rider', 'kitchen', 'customer_service', 'dispatcher'].includes(r.role));
      setIsAdmin(isUserAdmin);
      const isUserSuperAdmin = roles.some(r => r.role === 'super_admin');
      setIsSuperAdmin(isUserSuperAdmin);
      const isUserRider = roles.some(r => r.role === 'rider');
      setIsRider(isUserRider);
    } catch (error) {
      console.error('Error refreshing user roles:', error);
    }
  };

  const hasRole = (role: string): boolean => {
    return userRoles?.some(r => r.role === role) ?? false;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return userRoles?.some(r => roles.includes(r.role)) ?? false;
  };

  const handleUserRedirect = useCallback((user: User, isUserAdmin: boolean, isUserRider: boolean) => {
    try {
      // Don't redirect if we're already on the right path
      const currentPath = window.location.pathname;
      const isAdminPath = currentPath.startsWith('/admin');
      const isRiderPath = currentPath.startsWith('/rider');
      
      // If user is rider, redirect to rider dashboard
      if (isUserRider && !isRiderPath) {
        const redirect = () => {
          if (currentPath !== '/rider/dashboard') {
            window.location.href = '/rider/dashboard';
          }
        };
        
        if (loading) {
          setTimeout(redirect, 300);
        } else {
          redirect();
        }
        return;
      }
      
      // If user is not rider but on rider path, redirect appropriately
      if (!isUserRider && isRiderPath) {
        if (isUserAdmin) {
          window.location.href = '/admin/dashboard';
        } else {
          window.location.href = '/';
        }
        return;
      }
      
      // Check if they can be a customer
      const canBeCustomer = userRoles?.some(r => ['customer'].includes(r.role)) || false;

      // If user is admin and not on an admin path, redirect to admin dashboard
      if (isUserAdmin && !isAdminPath && !canBeCustomer) {
        const redirect = () => {
          if (currentPath !== '/admin/dashboard') {
            window.location.href = '/admin/dashboard';
          }
        };
        
        if (loading) {
          setTimeout(redirect, 300);
        } else {
          redirect();
        }
      }
      // If user is not admin and is on an admin path, redirect to home
      else if (!isUserAdmin && isAdminPath) {
        window.location.href = '/';
      }
      // Customer redirect removed - handled by AuthModal to prevent conflicts
    } catch (error) {
      console.error('Error in handleUserRedirect:', error);
    }
  }, [loading, userRoles]);

  const signOut = async () => {
    // Clear relevant localStorage items
    try {
      // Clear product comparison data
      localStorage.removeItem('product_comparison');
      
      // Clear recently viewed products
      localStorage.removeItem('recently_viewed_products');
      
      // Clear any cart data that might be stored
      const userCartKey = user?.id ? `cart_${user.id}` : null;
      if (userCartKey) {
        localStorage.removeItem(userCartKey);
      }
      
      // You may want to keep some items like theme preference

      // Clear branch selection
      localStorage.removeItem('selectedBranchId');
      
      // Don't clear all localStorage - let Supabase handle its own auth data
      
      console.log('localStorage items cleared successfully');
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
    
    // Sign out from Supabase
    await supabase.auth.signOut();
    
    // Redirect based on user type
    if (isAdmin || isSuperAdmin || isRider) {
      // Admin/staff users go to admin auth page
      window.location.href = '/auth';
    } else {
      // Regular customers go to homepage
      window.location.href = '/';
    }
  };

  const value = {
    session,
    user,
    signOut,
    loading,
    isAdmin,
    userRoles,
    isSuperAdmin,
    isRider,
    isEmailConfirmed,
    hasRole,
    hasAnyRole,
    refreshUserRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
